import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Calendar,
  Plus,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  Circle,
  AlertTriangle,
  Flag,
  X,
  Trash2,
  Edit3,
  ListChecks
} from 'lucide-react';

const EVENT_TYPES = [
  { value: 'audit', label: 'Audit', color: 'bg-blue-500' },
  { value: 'certification', label: 'Certification', color: 'bg-green-500' },
  { value: 'review', label: 'Policy Review', color: 'bg-purple-500' },
  { value: 'training', label: 'Training', color: 'bg-yellow-500' },
  { value: 'other', label: 'Other', color: 'bg-slate-500' }
];

const ComplianceCalendar = () => {
  const { notify } = useNotification();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    eventType: 'audit',
    date: '',
    priority: 'medium',
    relatedFramework: '',
    milestones: []
  });
  const [newMilestone, setNewMilestone] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await calendarAPI.getAll();
      setEvents(res.data || []);
    } catch (err) {
      notify.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedEvent) {
        await calendarAPI.update(selectedEvent.id, eventForm);
        notify.success('Event updated');
      } else {
        await calendarAPI.create(eventForm);
        notify.success('Event created');
      }
      setShowEventModal(false);
      setSelectedEvent(null);
      setEventForm({ title: '', description: '', eventType: 'audit', date: '', priority: 'medium', relatedFramework: '', milestones: [] });
      fetchEvents();
    } catch (err) {
      notify.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await calendarAPI.delete(eventId);
      notify.success('Event deleted');
      fetchEvents();
    } catch (err) {
      notify.error('Failed to delete');
    }
  };

  const handleToggleMilestone = async (eventId, milestoneId) => {
    try {
      await calendarAPI.toggleMilestone(eventId, milestoneId);
      fetchEvents();
    } catch (err) {
      notify.error('Failed to update milestone');
    }
  };

  const addMilestoneToForm = () => {
    if (!newMilestone.trim()) return;
    setEventForm({
      ...eventForm,
      milestones: [...eventForm.milestones, { id: Date.now().toString(), title: newMilestone, completed: false }]
    });
    setNewMilestone('');
  };

  const removeMilestoneFromForm = (idx) => {
    setEventForm({
      ...eventForm,
      milestones: eventForm.milestones.filter((_, i) => i !== idx)
    });
  };

  // Calendar helpers
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

  const getEventsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date?.startsWith(dateStr));
  };

  const renderCalendar = () => {
    const days = [];
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const today = new Date();

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-slate-800/30"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = today.getDate() === day && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();
      const dayEvents = getEventsForDay(day);

      days.push(
        <div
          key={day}
          onClick={() => {
            setSelectedDate(day);
            setEventForm({ ...eventForm, date: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` });
          }}
          className={`h-24 p-2 border border-slate-700 cursor-pointer hover:bg-slate-700/50 transition-colors ${
            isToday ? 'bg-violet-500/20 border-violet-500' : 'bg-slate-800/50'
          }`}
        >
          <div className={`text-sm font-medium ${isToday ? 'text-violet-400' : 'text-slate-300'}`}>{day}</div>
          <div className="mt-1 space-y-1 overflow-hidden">
            {dayEvents.slice(0, 2).map(event => {
              const typeConfig = EVENT_TYPES.find(t => t.value === event.eventType) || EVENT_TYPES[4];
              return (
                <div
                  key={event.id}
                  onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); setEventForm(event); setShowEventModal(true); }}
                  className={`text-xs px-1 py-0.5 rounded truncate text-white ${typeConfig.color}`}
                >
                  {event.title}
                </div>
              );
            })}
            {dayEvents.length > 2 && <div className="text-xs text-slate-500">+{dayEvents.length - 2} more</div>}
          </div>
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="w-10 h-10 text-violet-500 animate-spin" /></div>;
  }

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date()).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-16">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-7 h-7 text-violet-400" />
              Compliance Calendar
            </h1>
            <p className="text-slate-400 mt-1">Track audits, certifications, and compliance deadlines</p>
          </div>
          <button
            onClick={() => { setSelectedEvent(null); setEventForm({ title: '', description: '', eventType: 'audit', date: '', priority: 'medium', relatedFramework: '', milestones: [] }); setShowEventModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
          >
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-3 bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <button onClick={prevMonth} className="p-2 hover:bg-slate-700 rounded-lg"><ChevronLeft className="w-5 h-5 text-slate-400" /></button>
              <h2 className="text-xl font-semibold text-white">{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
              <button onClick={nextMonth} className="p-2 hover:bg-slate-700 rounded-lg"><ChevronRight className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-7 gap-px mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} className="text-center text-sm font-medium text-slate-500 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-px">{renderCalendar()}</div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-violet-400" /> Upcoming
            </h3>
            {upcomingEvents.length === 0 ? (
              <p className="text-slate-500 text-sm">No upcoming events</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map(event => {
                  const typeConfig = EVENT_TYPES.find(t => t.value === event.eventType) || EVENT_TYPES[4];
                  return (
                    <div key={event.id} onClick={() => { setSelectedEvent(event); setEventForm(event); setShowEventModal(true); }} className="p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${typeConfig.color}`}></div>
                        <span className="text-white text-sm font-medium truncate">{event.title}</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-1">{new Date(event.date).toLocaleDateString()}</p>
                      {event.milestones?.length > 0 && (
                        <div className="mt-2 text-xs text-slate-400">
                          <ListChecks className="w-3 h-3 inline mr-1" />
                          {event.milestones.filter(m => m.completed).length}/{event.milestones.length} milestones
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700 shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">{selectedEvent ? 'Edit Event' : 'New Event'}</h3>
                <button onClick={() => setShowEventModal(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSaveEvent} className="p-6 space-y-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Title *</label>
                  <input type="text" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Type</label>
                    <select value={eventForm.eventType} onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                      {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm mb-1">Date *</label>
                    <input type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" required />
                  </div>
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Description</label>
                  <textarea value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} rows={2} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none" />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Framework</label>
                  <input type="text" placeholder="e.g., HIPAA, CMMC, NIST" value={eventForm.relatedFramework} onChange={(e) => setEventForm({ ...eventForm, relatedFramework: e.target.value })} className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white" />
                </div>
                {/* Milestones */}
                <div>
                  <label className="block text-slate-300 text-sm mb-2">Milestones Checklist</label>
                  <div className="flex gap-2 mb-2">
                    <input type="text" placeholder="Add milestone..." value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMilestoneToForm())} className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm" />
                    <button type="button" onClick={addMilestoneToForm} className="px-3 py-2 bg-violet-600 text-white rounded-lg"><Plus className="w-4 h-4" /></button>
                  </div>
                  {eventForm.milestones?.length > 0 && (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {eventForm.milestones.map((m, idx) => (
                        <div key={m.id || idx} className="flex items-center gap-2 p-2 bg-slate-700/50 rounded-lg">
                          <Circle className="w-4 h-4 text-slate-500" />
                          <span className="text-sm text-slate-300 flex-1">{m.title}</span>
                          <button type="button" onClick={() => removeMilestoneFromForm(idx)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 pt-4">
                  {selectedEvent && (
                    <button type="button" onClick={() => handleDeleteEvent(selectedEvent.id)} className="px-4 py-2 border border-red-500 text-red-400 rounded-lg hover:bg-red-500/10">Delete</button>
                  )}
                  <button type="button" onClick={() => setShowEventModal(false)} className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ComplianceCalendar;
